import IconButton from 'components/iconbutton/IconButton';
import CheckIcon from 'components/icons/CheckIcon';
import CloseIcon from 'components/icons/CloseIcon';
import EyeIcon from 'components/icons/EyeIcon';
import EyeSlashIcon from 'components/icons/EyeSlashIcon';
import PenIcon from 'components/icons/PenIcon';
import React, { HTMLInputTypeAttribute, useEffect, useState } from 'react';
import { ASComponentSize } from 'types/App.types';
import styles from './EditableField.module.scss';

type EditableFieldProps = {
  className?: string;
  fieldValue: string;
  onFieldChange?: (value: string) => void;
  showEditButtonWithLowOpacity?: boolean;
  multiline?: boolean;
  editingMode?: boolean;
  preventExitEditModeWhenEmptyString?: boolean;
  requiredMessage?: string;
  type?: HTMLInputTypeAttribute;
} & React.InputHTMLAttributes<HTMLInputElement>;

const EditableField: React.FC<EditableFieldProps> = ({
  className = '',
  showEditButtonWithLowOpacity = false,
  fieldValue,
  onFieldChange = () => undefined,
  multiline = false,
  editingMode = false,
  preventExitEditModeWhenEmptyString = false,
  requiredMessage = 'Field cannot be empty',
  ...props
}) => {
  const baseCls = multiline
    ? `${styles.EditableFieldContainerMultiLine} ${className}`
    : `${styles.EditableFieldContainer} ${className}`;

  const [value, setValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isPasswordField, setIsPasswordField] = useState(false);
  const [revealPassword, setRevealPassword] = useState(false);

  const hideError = () => setShowError(false);

  const onValueChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  useEffect(() => {
    setIsEditing(editingMode);
    setIsPasswordField(props.type === 'password');
  }, [editingMode, props.type]);

  useEffect(() => {
    setValue(fieldValue);
  }, [fieldValue]);

  const onCancelPressed = () => {
    setValue(fieldValue);
    hideError();
    setIsEditing(false);
  };
  const onSavePressed = () => {
    if (preventExitEditModeWhenEmptyString && value.trim() === '') {
      setValue('');
      setShowError(true);
      return;
    }
    hideError();
    setIsEditing(false);
    onFieldChange(value);
  };

  const onShowHidePasswordText = () => {
    setRevealPassword(!revealPassword);
  };

  const editFieldsCommonProps = { onFocus: hideError, value: value, onChange: onValueChange };

  const renderTextArea = () => {
    const textAreaCls = `${styles.EditableFieldWithSmallButtons} ${styles.EditableFieldTextArea}`;
    return <textarea aria-multiline="true" className={textAreaCls} {...editFieldsCommonProps} />;
  };

  const renderTextInput = () => {
    const textInputCls = `${styles.EditableFieldWithSmallButtons} ${styles.EditableFieldInput}`;
    const locFieldType = props.type ? (props.type as HTMLInputTypeAttribute) : 'text';
    const fieldType = isPasswordField ? (!revealPassword ? 'password' : 'text') : locFieldType;
    return <input className={textInputCls} {...editFieldsCommonProps} {...props} type={fieldType} />;
  };

  const renderInEditMode = () => {
    return (
      <>
        {multiline ? renderTextArea() : renderTextInput()}
        <IconButton
          className={styles.EditableFieldIconBtn}
          aria-label="Okay button indicating user has completed the interaction"
          size={ASComponentSize.Small}
          onClick={onSavePressed}
        >
          <CheckIcon size={40} fillColor={'transparent'} />
        </IconButton>
        {!preventExitEditModeWhenEmptyString && (
          <IconButton
            className={styles.EditableFieldIconBtn}
            aria-label="Cancel button indicating user wants to revert to the previous state"
            size={ASComponentSize.Small}
            onClick={onCancelPressed}
          >
            <CloseIcon size={40} />
          </IconButton>
        )}
        {showError && <span className={styles.EditableFieldErrorField}>{requiredMessage}</span>}
        {isPasswordField && (
          <IconButton
            className={styles.EditableFieldIconBtn}
            aria-label="Button to show or hide the password text entered in the input field"
            size={ASComponentSize.Small}
            onClick={onShowHidePasswordText}
          >
            {revealPassword ? <EyeSlashIcon size={40} /> : <EyeIcon size={40} />}
          </IconButton>
        )}
      </>
    );
  };

  const renderInReadMode = () => {
    const textCls = multiline ? styles.EditableFieldValueMultiLine : styles.EditableFieldValue;
    const valueLen = value.length;
    const starString = Array.from(Array(valueLen), () => '*').join('');
    const showString = isPasswordField && !revealPassword ? starString : value;
    return (
      <>
        <span className={textCls}>{showString}</span>
        <IconButton
          defaultLowOpacity={showEditButtonWithLowOpacity}
          className={styles.EditableFieldIconBtn}
          size={ASComponentSize.Small}
          onClick={() => setIsEditing(true)}
        >
          <PenIcon size={40} />
        </IconButton>
        {isPasswordField && (
          <IconButton
            defaultLowOpacity={showEditButtonWithLowOpacity}
            className={styles.EditableFieldIconBtn}
            size={ASComponentSize.Small}
            onClick={onShowHidePasswordText}
          >
            {revealPassword ? <EyeSlashIcon size={40} /> : <EyeIcon size={40} />}
          </IconButton>
        )}
      </>
    );
  };

  return <div className={baseCls}>{isEditing ? renderInEditMode() : renderInReadMode()}</div>;
};

export default EditableField;
